import { useState, useMemo } from 'react'
import {
  Navbar,
  Block,
  BlockTitle,
  List,
  ListItem,
  Badge,
  Sheet,
  Button,
  Dialog,
  DialogButton,
} from 'konsta/react'
import {
  ShieldAlert,
  AlertTriangle,
  Clock,
  CheckCircle2,
  XCircle,
  User,
  Calendar,
  Eye,
  Ban,
  FileText,
  Shield,
  Wifi,
  Brain,
  Users,
  DollarSign,
  ArrowRight,
  Activity,
} from 'lucide-react'
import { formatRelativeTime, cn } from '@/lib/utils'

// --- Types ---

type AlertSeverity = 'high' | 'medium' | 'low'
type AlertStatus = 'investigating' | 'confirmed' | 'cleared'

interface FraudAlert {
  id: string
  user: string
  type: string
  severity: AlertSeverity
  date: Date
  status: AlertStatus
  details: string
}

// --- Mock Data ---

const mockAlerts: FraudAlert[] = [
  {
    id: 'F001',
    user: 'Unknown-88',
    type: '多帳號登入',
    severity: 'high',
    date: new Date(Date.now() - 1000 * 60 * 60),
    status: 'investigating',
    details: '同一 IP 地址檢測到 3 個不同帳號登入。',
  },
  {
    id: 'F002',
    user: 'Alex Kim',
    type: '異常投注模式',
    severity: 'high',
    date: new Date(Date.now() - 1000 * 60 * 60 * 3),
    status: 'confirmed',
    details: '連續 20 手完美決策，疑似使用 AI 輔助。',
  },
  {
    id: 'F003',
    user: 'Player-456',
    type: '串通行為',
    severity: 'medium',
    date: new Date(Date.now() - 1000 * 60 * 60 * 12),
    status: 'investigating',
    details: '兩名玩家在同桌時出現異常配合行為模式。',
  },
  {
    id: 'F004',
    user: 'NewUser-99',
    type: '快速大額儲值',
    severity: 'medium',
    date: new Date(Date.now() - 1000 * 60 * 60 * 24),
    status: 'cleared',
    details: '新帳號首日儲值 $5,000，已確認為合法來源。',
  },
  {
    id: 'F005',
    user: 'Player-789',
    type: 'VPN 使用',
    severity: 'low',
    date: new Date(Date.now() - 1000 * 60 * 60 * 48),
    status: 'cleared',
    details: '檢測到 VPN 連接，已確認為商務旅行。',
  },
]

// --- Helpers ---

const severityConfig: Record<AlertSeverity, { label: string; color: string; bgColor: string }> = {
  high: { label: '高', color: '#FF3B30', bgColor: 'bg-[#FF3B30]/10' },
  medium: { label: '中', color: '#FF9500', bgColor: 'bg-[#FF9500]/10' },
  low: { label: '低', color: '#FFCC00', bgColor: 'bg-[#FFCC00]/15' },
}

const statusConfig: Record<AlertStatus, { label: string; color: string; bgColor: string }> = {
  investigating: { label: '調查中', color: '#007AFF', bgColor: 'bg-[#007AFF]/10' },
  confirmed: { label: '已確認', color: '#FF3B30', bgColor: 'bg-[#FF3B30]/10' },
  cleared: { label: '已排除', color: '#34C759', bgColor: 'bg-[#34C759]/10' },
}

function SeverityBadge({ severity }: { severity: AlertSeverity }) {
  const config = severityConfig[severity]
  return (
    <Badge
      colors={{ bg: config.bgColor.replace('bg-', '') }}
      className="!font-semibold"
      style={{ color: config.color, backgroundColor: `${config.color}18` }}
    >
      {config.label}風險
    </Badge>
  )
}

function StatusBadge({ status }: { status: AlertStatus }) {
  const config = statusConfig[status]
  const Icon = status === 'investigating' ? Clock : status === 'confirmed' ? XCircle : CheckCircle2
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full',
        config.bgColor
      )}
      style={{ color: config.color }}
    >
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  )
}

function getAlertIcon(type: string) {
  switch (type) {
    case '多帳號登入':
      return Users
    case '異常投注模式':
      return Brain
    case '串通行為':
      return Users
    case '快速大額儲值':
      return DollarSign
    case 'VPN 使用':
      return Wifi
    default:
      return AlertTriangle
  }
}

// --- Detail Sheet ---

function FraudDetailSheet({
  alert,
  open,
  onClose,
  onBan,
}: {
  alert: FraudAlert | null
  open: boolean
  onClose: () => void
  onBan: () => void
}) {
  if (!alert) return null

  const Icon = getAlertIcon(alert.type)
  const sevConfig = severityConfig[alert.severity]

  return (
    <Sheet
      opened={open}
      onBackdropClick={onClose}
      className="pb-safe"
      style={{ height: 'auto', maxHeight: '90vh' }}
    >
      <div className="overflow-y-auto" style={{ maxHeight: '85vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5EA]">
          <h3 className="text-lg font-bold text-black">警報詳情</h3>
          <button onClick={onClose} className="text-sm font-medium text-[#007AFF]">
            關閉
          </button>
        </div>

        {/* Alert Header */}
        <Block className="!mt-2">
          <div className="flex items-center gap-3 mb-4">
            <div
              className={cn('w-12 h-12 rounded-xl flex items-center justify-center', severityConfig[alert.severity].bgColor)}
            >
              <Icon className="w-6 h-6" style={{ color: sevConfig.color }} />
            </div>
            <div className="flex-1">
              <p className="text-base font-bold text-black">{alert.type}</p>
              <p className="text-sm text-[#8E8E93]">{alert.id}</p>
            </div>
            <SeverityBadge severity={alert.severity} />
          </div>

          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4 text-[#8E8E93]" />
              <span className="text-[#8E8E93]">涉及用戶</span>
              <span className="font-medium text-black ml-auto">{alert.user}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <ShieldAlert className="w-4 h-4 text-[#8E8E93]" />
              <span className="text-[#8E8E93]">狀態</span>
              <span className="ml-auto"><StatusBadge status={alert.status} /></span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-[#8E8E93]" />
              <span className="text-[#8E8E93]">檢測時間</span>
              <span className="font-medium text-black ml-auto">
                {formatRelativeTime(alert.date)}
              </span>
            </div>
          </div>
        </Block>

        {/* Details */}
        <BlockTitle className="flex items-center gap-1.5">
          <Eye className="w-4 h-4" />
          詳細說明
        </BlockTitle>
        <Block strong inset>
          <p className="text-sm text-black leading-relaxed">{alert.details}</p>
        </Block>

        {/* Evidence */}
        <BlockTitle className="flex items-center gap-1.5">
          <FileText className="w-4 h-4" />
          相關證據
        </BlockTitle>
        <Block strong inset>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Activity className="w-4 h-4 text-[#007AFF]" />
              <span className="text-[#007AFF] font-medium">行為分析報告</span>
              <ArrowRight className="w-3 h-3 text-[#8E8E93] ml-auto" />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <FileText className="w-4 h-4 text-[#007AFF]" />
              <span className="text-[#007AFF] font-medium">IP 登入記錄</span>
              <ArrowRight className="w-3 h-3 text-[#8E8E93] ml-auto" />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <FileText className="w-4 h-4 text-[#007AFF]" />
              <span className="text-[#007AFF] font-medium">手牌歷史記錄</span>
              <ArrowRight className="w-3 h-3 text-[#8E8E93] ml-auto" />
            </div>
          </div>
        </Block>

        {/* User History */}
        <BlockTitle className="flex items-center gap-1.5">
          <User className="w-4 h-4" />
          用戶歷史
        </BlockTitle>
        <Block strong inset>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[#8E8E93]">帳號建立</span>
              <span className="font-medium text-black">30 天前</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#8E8E93]">總遊戲手數</span>
              <span className="font-medium text-black">482 手</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#8E8E93]">過往警報</span>
              <span className="font-medium text-[#FF9500]">1 次</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#8E8E93]">勝率</span>
              <span className="font-medium text-black">68.3%</span>
            </div>
          </div>
        </Block>

        {/* Actions */}
        {alert.status === 'investigating' && (
          <Block className="!px-4 !mt-4 !mb-4">
            <div className="flex gap-3">
              <Button className="flex-1" large rounded tonal>
                <span className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  標記已排除
                </span>
              </Button>
              <Button
                className="flex-1"
                large
                rounded
                onClick={onBan}
                colors={{ fillBgIos: 'bg-[#FF3B30] active:bg-[#FF3B30]/80', fillTextIos: 'text-white' }}
              >
                <span className="flex items-center gap-2">
                  <Ban className="w-4 h-4" />
                  封禁帳號
                </span>
              </Button>
            </div>
          </Block>
        )}

        {alert.status === 'confirmed' && (
          <Block className="!px-4 !mt-4 !mb-4">
            <Button
              large
              rounded
              colors={{ fillBgIos: 'bg-[#FF3B30] active:bg-[#FF3B30]/80', fillTextIos: 'text-white' }}
              onClick={onBan}
            >
              <span className="flex items-center gap-2">
                <Ban className="w-4 h-4" />
                封禁帳號
              </span>
            </Button>
          </Block>
        )}

        <div className="h-4" />
      </div>
    </Sheet>
  )
}

// --- Main Screen ---

export function AdminFraudScreen() {
  const [selectedAlert, setSelectedAlert] = useState<FraudAlert | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [banDialogOpen, setBanDialogOpen] = useState(false)

  const counts = useMemo(() => ({
    total: mockAlerts.length,
    investigating: mockAlerts.filter((a) => a.status === 'investigating').length,
    confirmed: mockAlerts.filter((a) => a.status === 'confirmed').length,
    cleared: mockAlerts.filter((a) => a.status === 'cleared').length,
  }), [])

  const handleAlertClick = (alert: FraudAlert) => {
    setSelectedAlert(alert)
    setSheetOpen(true)
  }

  const closeSheet = () => {
    setSheetOpen(false)
    setSelectedAlert(null)
  }

  const handleBan = () => {
    setBanDialogOpen(true)
  }

  const confirmBan = () => {
    setBanDialogOpen(false)
    closeSheet()
  }

  return (
    <>
      <Navbar title="反欺詐監控" />

      <div className="overflow-y-auto pb-8">
        {/* Summary Cards */}
        <Block className="!mt-4 !mb-2">
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-white rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-black">{counts.total}</p>
              <p className="text-[10px] text-[#8E8E93] mt-0.5">本月警報</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-[#007AFF]">{counts.investigating}</p>
              <p className="text-[10px] text-[#8E8E93] mt-0.5">調查中</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-[#FF3B30]">{counts.confirmed}</p>
              <p className="text-[10px] text-[#8E8E93] mt-0.5">已確認</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-[#34C759]">{counts.cleared}</p>
              <p className="text-[10px] text-[#8E8E93] mt-0.5">已排除</p>
            </div>
          </div>
        </Block>

        {/* Alert List */}
        <BlockTitle>欺詐警報</BlockTitle>
        <List strongIos insetIos>
          {mockAlerts.map((alert) => {
            const Icon = getAlertIcon(alert.type)
            const sevConfig = severityConfig[alert.severity]
            return (
              <ListItem
                key={alert.id}
                link
                chevronIos
                onClick={() => handleAlertClick(alert)}
                title={
                  <span className="flex items-center gap-2">
                    <span className="font-semibold text-black">{alert.type}</span>
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                      style={{
                        color: sevConfig.color,
                        backgroundColor: `${sevConfig.color}18`,
                      }}
                    >
                      {sevConfig.label}
                    </span>
                  </span>
                }
                subtitle={
                  <span className="flex items-center gap-2 text-[#8E8E93]">
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {alert.user}
                    </span>
                    <span>|</span>
                    <StatusBadge status={alert.status} />
                  </span>
                }
                media={
                  <div
                    className={cn(
                      'w-10 h-10 rounded-xl flex items-center justify-center',
                      sevConfig.bgColor
                    )}
                  >
                    <Icon className="w-5 h-5" style={{ color: sevConfig.color }} />
                  </div>
                }
                text={
                  <span className="flex items-center gap-1 text-[#8E8E93]">
                    <Clock className="w-3 h-3" />
                    {formatRelativeTime(alert.date)}
                  </span>
                }
              />
            )
          })}
        </List>
      </div>

      {/* Detail Sheet */}
      <FraudDetailSheet
        alert={selectedAlert}
        open={sheetOpen}
        onClose={closeSheet}
        onBan={handleBan}
      />

      {/* Ban Confirmation Dialog */}
      <Dialog
        opened={banDialogOpen}
        onBackdropClick={() => setBanDialogOpen(false)}
        title="確認封禁帳號"
        content={`確定封禁用戶「${selectedAlert?.user}」？此操作將立即凍結該帳號的所有資金並禁止登入。`}
        buttons={
          <>
            <DialogButton onClick={() => setBanDialogOpen(false)}>取消</DialogButton>
            <DialogButton
              strong
              onClick={confirmBan}
              className="text-[#FF3B30]"
            >
              確認封禁
            </DialogButton>
          </>
        }
      />
    </>
  )
}
